/**
 * PURE inventory logic (F3): low-stock, applying movements
 * (in/out/transfer/adjust) and summary. The movement is the inventory
 * LEDGER (audited by activity-core); the item's quantity is the
 * materialized state the client updates in the SAME local transaction.
 */

export type InventoryMovementKind = "in" | "out" | "transfer" | "adjust";
export type InventoryItemKind = "permanent" | "consumable";

export interface InventoryItemLike {
  id: string;
  name?: string | null;
  kind: string | null;
  category?: string | null;
  quantity: number | null;
  min_quantity: number | null;
  venue_id: string | null;
}

export interface MovementInput {
  kind: InventoryMovementKind;
  /** Units of the movement (`adjust` = absolute count). Always ≥ 0. */
  quantity: number;
  toVenueId?: string | null;
}

export interface MovementResult {
  quantity: number;
  venueId: string | null;
}

/**
 * Low stock: threshold active (`min_quantity > 0`) and stock AT or BELOW the
 * threshold. Applies to any item, although the typical case is consumables.
 */
export function isLowStock(item: Pick<InventoryItemLike, "quantity" | "min_quantity">): boolean {
  const min = item.min_quantity ?? 0;
  return min > 0 && (item.quantity ?? 0) <= min;
}

/**
 * Applies a movement to the item's state. Returns the new state or throws
 * if the movement is impossible (negative quantity, removal greater than the
 * stock): the ledger does not record movements that could not happen.
 */
export function applyMovement(
  item: Pick<InventoryItemLike, "quantity" | "venue_id">,
  movement: MovementInput,
): MovementResult {
  const current = item.quantity ?? 0;
  if (!Number.isInteger(movement.quantity) || movement.quantity < 0) {
    throw new RangeError(`movement quantity must be a non-negative integer`);
  }
  switch (movement.kind) {
    case "in": {
      return { quantity: current + movement.quantity, venueId: item.venue_id ?? null };
    }
    case "out": {
      if (movement.quantity > current) {
        throw new RangeError(`cannot remove ${movement.quantity} from ${current} in stock`);
      }
      return { quantity: current - movement.quantity, venueId: item.venue_id ?? null };
    }
    case "transfer": {
      // A transfer does not change stock: it changes the item's LOCATION.
      return { quantity: current, venueId: movement.toVenueId ?? null };
    }
    default: {
      // Physical count ("adjust"): the movement's quantity IS the new total.
      return { quantity: movement.quantity, venueId: item.venue_id ?? null };
    }
  }
}

export interface InventorySummary {
  totalItems: number;
  totalUnits: number;
  permanentItems: number;
  consumableItems: number;
  /** Items at or below their threshold (order: most critical first). */
  lowStock: InventoryItemLike[];
}

/** Summary of the visible inventory (for the header and low-stock alerts). */
export function inventorySummary(items: readonly InventoryItemLike[]): InventorySummary {
  const lowStock = items
    .filter((i) => isLowStock(i))
    .toSorted(
      (a, b) =>
        (a.quantity ?? 0) - (a.min_quantity ?? 0) - ((b.quantity ?? 0) - (b.min_quantity ?? 0)),
    );
  return {
    totalItems: items.length,
    totalUnits: items.reduce((sum, i) => sum + (i.quantity ?? 0), 0),
    permanentItems: items.filter((i) => i.kind !== "consumable").length,
    consumableItems: items.filter((i) => i.kind === "consumable").length,
    lowStock,
  };
}
